// services/OrganizationBucketService.js
// ✅ Uses EXISTING endpoint: GET /api/v1.0/organization/{orgId}/aws-config

import { organizationCachedAxios } from '../../../shared/config/cachedAxiosConfig';
import { getAuthHeaders } from '../../../shared/services/auth/authHeaders';
import { AWS_CONFIG_ENDPOINTS } from '../config/IntegrationEndpoints';

/**
 * Service to fetch and manage organization bucket configuration
 * Uses your EXISTING OrganizationConfigController.getAWSConfig() endpoint
 */
export const OrganizationBucketService = {
  /**
   * Fetch AWS configuration including custom bucket settings
   * Calls: GET /api/v1.0/organization/{orgId}/aws-config
   * @param {string} organizationId - The organization/tenant ID
   * @returns {Promise<Object>} AWS config with bucket info
   */
  fetchOrganizationConfig: async (organizationId, divisionId) => {
    if (!organizationId || !divisionId) {
      console.warn('⚠️ No organization ID or division ID provided to fetchOrganizationConfig');
      return {
        success: false,
        error: 'Organization ID and Division ID are required'
      };
    }

    try {
      const headers = await getAuthHeaders();

      // ✅ CRITICAL: Add X-Tenant-Id header (your backend requires this)
      headers['X-Tenant-Id'] = organizationId;

      console.log(`📡 Fetching AWS config for: ${organizationId} division: ${divisionId}`);

      // ✅ Using your EXISTING aws-config endpoint from IntegrationEndpoints
      const url = AWS_CONFIG_ENDPOINTS.getAWSConfig(organizationId, divisionId);
      console.log(`📡 Calling endpoint: ${url}`);
      
      const response = await organizationCachedAxios.get(url, { headers });
      
      if (response.data) {
        console.log('✅ AWS config fetched:', {
          customBuckets: response.data.customBuckets,
          projectsBucketName: response.data.projectsBucketName,
          projectsRoleArn: response.data.projectsRoleArn
        });
        
        return {
          success: true,
          data: response.data
        };
      }
      
      return {
        success: false,
        error: 'No AWS config data returned'
      };
      
    } catch (error) {
      console.error('❌ Error fetching AWS config:', error);
      
      // Handle specific error cases
      if (error.response?.status === 403 || error.response?.status === 401) {
        console.error('🔒 Permission denied - Check IAM role permissions');
      } else if (error.response?.status === 404) {
        console.error('🔍 Organization not found:', organizationId);
      }
      
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch AWS config'
      };
    }
  },

  /**
   * Extract bucket configuration from AWS config response
   * Maps the AWSConfigResponse from your OrganizationConfigController
   * @param {Object} awsConfigData - AWS config data from API (AWSConfigResponse)
   * @returns {Object} Bucket configuration object
   */
  extractBucketConfig: (awsConfigData) => {
    if (!awsConfigData) {
      return {
        customBuckets: false,
        projectsBucketName: null,
        projectsBucketRoleArn: null
      };
    }

    // Map the response fields from your AWSConfigResponse DTO
    return {
      customBuckets: awsConfigData.customBuckets === true,
      projectsBucketName: awsConfigData.projectsBucketName || null,
      projectsBucketRoleArn: awsConfigData.projectsRoleArn || null // ⚠️ API uses projectsRoleArn not projectsBucketRoleArn
    };
  },

  /**
   * Update user data with AWS bucket configuration
   * This merges AWS bucket config into userData
   * @param {Object} userData - Current user data
   * @param {Object} awsConfigData - AWS config data from API
   * @returns {Object} Updated user data with bucket config
   */
  mergeOrgBucketConfig: (userData, awsConfigData) => {
    const bucketConfig = OrganizationBucketService.extractBucketConfig(awsConfigData);
    
    console.log('🔄 Merging bucket config into userData:', bucketConfig);
    
    return {
      ...userData,
      customBuckets: bucketConfig.customBuckets,
      projectsBucketName: bucketConfig.projectsBucketName,
      projectsBucketRoleArn: bucketConfig.projectsBucketRoleArn,
      // Also update currentOrganization if it exists
      currentOrganization: userData.currentOrganization ? {
        ...userData.currentOrganization,
        customBuckets: bucketConfig.customBuckets,
        projectsBucketName: bucketConfig.projectsBucketName,
        projectsBucketRoleArn: bucketConfig.projectsBucketRoleArn
      } : null
    };
  },

  /**
   * Determine which bucket should be used for this organization
   * @param {Object} userData - User data with organization info
   * @returns {string} The correct bucket name to use
   */
  getBucketNameForOrg: (userData) => {
    // Check if custom buckets are enabled and configured
    const hasCustomBucket = userData?.customBuckets === true ||
                           userData?.currentOrganization?.customBuckets === true;
    
    const customBucketName = userData?.projectsBucketName ||
                            userData?.currentOrganization?.projectsBucketName;
    
    if (hasCustomBucket && customBucketName) {
      console.log('📦 Using CUSTOM bucket:', customBucketName);
      return customBucketName;
    }
    
    // Fallback to default bucket, then genAI bucket
    const defaultBucket = userData?.fileManagementBucket || userData?.conversationsBucket || null;

    console.log('📦 Using DEFAULT bucket:', defaultBucket);
    return defaultBucket;
  }
};
